/**
 * RepcoImportModal
 *
 * Three-step wizard for importing a Repco SmartQuote into a job as work items.
 *
 *   1. Upload      — pick the CSV file + paste the service-operations text
 *   2. Preview     — review/edit the proposed work items + parts
 *   3. Committing  — spinner while the backend writes JobWorkItem rows
 *
 * On successful commit the modal closes itself and the caller is expected to
 * refresh the job (e.g. via onRefreshJob), so the new work items appear on
 * the Work Items tab immediately.
 *
 * Visual language mirrors JobDetailModal — same `bg-black/60` backdrop,
 * rounded-2xl card, violet accents, lucide icons, lift-on-hover buttons.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    X, Upload, FileText, Package, ChevronLeft, ChevronDown, RefreshCw,
    AlertTriangle, CheckCircle, Wrench, Trash2, Info, Send,
} from 'lucide-react';

import {
    previewRepcoImport,
    commitRepcoImport,
    closeModal,
    clearError,
    editWorkItemField,
    removeWorkItem,
    editPartField,
    removePart,
} from '../store/slices/repcoImportSlice';
import useEscapeToClose from './useEscapeToClose';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Coerce a value to a number for display; falls back to 0. */
const num = (v) => (v == null || v === '' || isNaN(Number(v)) ? 0 : Number(v));

/** Format money with two decimals and an A$ prefix. */
const money = (v) => `A$${num(v).toFixed(2)}`;

/** Per-work-item total derived from current edited values — preview only. */
const computeWorkItemTotal = (wi) => {
    const labour = num(wi.labourHours) * num(wi.labourRate);
    const parts = (wi.parts || []).reduce((acc, p) => acc + num(p.lineTotal), 0);
    return labour + parts;
};

// ── UploadStep ────────────────────────────────────────────────────────────────

const UploadStep = ({ csvFile, setCsvFile, operationsText, setOperationsText, onCancel, onPreview, isLoading, error }) => {
    const fileInputRef = React.useRef(null);
    const canSubmit = csvFile && operationsText.trim().length > 0 && !isLoading;

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        if (file) setCsvFile(file);
    };

    return (
        <>
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                        <div>{error}</div>
                    </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800 flex items-start gap-2">
                    <Info size={16} className="shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium mb-1">How to prepare your import</p>
                        <ol className="list-decimal pl-4 space-y-0.5 text-blue-900/90">
                            <li>In Repco SmartQuote, click <span className="font-semibold">Export to CSV</span> and save the file</li>
                            <li>From the same quote screen, copy the service-operations block (intervals, sections, ticks)</li>
                            <li>Upload the CSV and paste the operations text below</li>
                        </ol>
                    </div>
                </div>

                {/* CSV upload */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Repco SmartQuote CSV
                    </label>
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl px-4 py-6 cursor-pointer transition-colors ${
                            csvFile
                                ? 'border-violet-300 bg-violet-50/50 hover:bg-violet-50'
                                : 'border-gray-300 hover:border-violet-400 hover:bg-violet-50/30'
                        }`}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleFile}
                            className="hidden"
                        />
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                                csvFile ? 'bg-violet-100 text-violet-600' : 'bg-gray-100 text-gray-500'
                            }`}>
                                {csvFile ? <FileText size={18} /> : <Upload size={18} />}
                            </div>
                            <div className="min-w-0 flex-1">
                                {csvFile ? (
                                    <>
                                        <p className="text-sm font-medium text-gray-900 truncate">{csvFile.name}</p>
                                        <p className="text-xs text-gray-500">{Math.round(csvFile.size / 1024)} KB · click to change</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm font-medium text-gray-700">Click to upload CSV file</p>
                                        <p className="text-xs text-gray-500">Repco SmartQuote export, .csv format</p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Operations text */}
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Service operations (paste from Repco)
                    </label>
                    <textarea
                        value={operationsText}
                        onChange={(e) => setOperationsText(e.target.value)}
                        placeholder={'Paste the service-operations block here.\n\nExample:\nEvery 10000 km or 12 months\nENGINE BAY OPERATIONS\n  □ Air filter   Renew\n  □ Engine oil   Drain/refill\n...'}
                        rows={10}
                        className="w-full border rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 resize-y"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                        Include all service intervals shown on the quote. This is used to group parts under the right service.
                    </p>
                </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex items-center gap-2 shrink-0">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-100"
                >
                    Cancel
                </button>
                <button
                    onClick={onPreview}
                    disabled={!canSubmit}
                    className="ml-auto flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading ? (
                        <><RefreshCw size={15} className="animate-spin" /> Generating preview…</>
                    ) : (
                        <><Send size={15} /> Generate preview</>
                    )}
                </button>
            </div>
        </>
    );
};

// ── PreviewStep ───────────────────────────────────────────────────────────────

const PreviewStep = ({ preview, editedWorkItems, onBack, onCommit, isCommitting, error }) => {
    const dispatch = useDispatch();

    // Include unmapped rows in the grand total — they'll become a trailing
    // "Miscellaneous" work item on commit, so the total shouldn't jump after.
    const unmappedTotal = useMemo(
        () => (preview?.unmappedCsvRows || []).reduce((acc, r) => acc + num(r.lineTotal), 0),
        [preview],
    );

    const grandTotal = useMemo(
        () =>
            (editedWorkItems || []).reduce((acc, wi) => acc + computeWorkItemTotal(wi), 0)
            + unmappedTotal,
        [editedWorkItems, unmappedTotal],
    );

    const hasMisc = (preview?.unmappedCsvRows || []).length > 0;
    const totalWorkItemsToCreate = (editedWorkItems || []).length + (hasMisc ? 1 : 0);
    const canCommit = (editedWorkItems || []).length > 0 && !isCommitting;

    return (
        <>
            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                        <div>{error}</div>
                    </div>
                )}

                {/* Quote summary */}
                <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 text-sm">
                    <div className="flex flex-wrap gap-x-6 gap-y-1">
                        <div>
                            <span className="text-violet-700/70 text-xs uppercase tracking-wide">Quote</span>
                            <p className="font-semibold text-violet-900">#{preview.quoteNumber || '—'}</p>
                        </div>
                        <div className="min-w-0 flex-1">
                            <span className="text-violet-700/70 text-xs uppercase tracking-wide">Vehicle</span>
                            <p className="font-medium text-violet-900 truncate" title={preview.vehicleName}>
                                {preview.vehicleName || '—'}
                            </p>
                        </div>
                        <div>
                            <span className="text-violet-700/70 text-xs uppercase tracking-wide">Intervals</span>
                            <p className="font-medium text-violet-900">
                                {(preview.intervalsCovered || []).length} covered
                            </p>
                        </div>
                    </div>
                </div>

                {(preview.warnings || []).length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
                        <div className="font-semibold mb-1 flex items-center gap-1.5">
                            <AlertTriangle size={14} /> A few things to check
                        </div>
                        <ul className="list-disc pl-5 space-y-0.5">
                            {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    </div>
                )}

                {/* Proposed work items */}
                <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        Proposed work items ({(editedWorkItems || []).length})
                    </p>
                    {(editedWorkItems || []).length === 0 ? (
                        <div className="text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-xl">
                            <Wrench size={20} className="mx-auto mb-2 opacity-40" />
                            All proposed items were removed. Go back to upload a different quote.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {editedWorkItems.map((wi) => (
                                <ProposedWorkItemCard key={wi.tempId} wi={wi} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Unmapped CSV rows — added as a "Miscellaneous" work item on commit */}
                {(preview.unmappedCsvRows || []).length > 0 && (
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Miscellaneous items ({preview.unmappedCsvRows.length})
                        </p>
                        <div className="border rounded-xl divide-y bg-white">
                            {preview.unmappedCsvRows.map((row, i) => (
                                <div key={i} className="px-3 py-2 flex items-start gap-3 text-sm">
                                    <Info size={14} className="text-gray-400 shrink-0 mt-1" />
                                    <div className="min-w-0 flex-1">
                                        <p className="font-mono text-xs text-gray-500">{row.partNumber}</p>
                                        <p className="text-gray-900 truncate">{row.description}</p>
                                        {row.reason && <p className="text-xs text-gray-500 italic mt-0.5">{row.reason}</p>}
                                    </div>
                                    <p className="font-mono text-sm text-gray-700 shrink-0">{money(row.lineTotal)}</p>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">
                            These rows will be added to a single <span className="font-semibold">Miscellaneous</span> work item at the end so nothing is lost. You can edit or remove parts on that item after import.
                        </p>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex items-center gap-2 shrink-0">
                <button
                    onClick={onBack}
                    disabled={isCommitting}
                    className="flex items-center gap-2 px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                    <ChevronLeft size={15} /> Back
                </button>
                <div className="ml-auto flex items-center gap-3">
                    <div className="text-right">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Estimated total</p>
                        <p className="text-lg font-bold text-gray-900">{money(grandTotal)}</p>
                    </div>
                    <button
                        onClick={onCommit}
                        disabled={!canCommit}
                        className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isCommitting ? (
                            <><RefreshCw size={15} className="animate-spin" /> Creating work items…</>
                        ) : (
                            <><CheckCircle size={15} /> Create {totalWorkItemsToCreate} work item{totalWorkItemsToCreate === 1 ? '' : 's'}</>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

// ── ProposedWorkItemCard ──────────────────────────────────────────────────────
//
// Collapsed by default — shows a compact summary row (description, interval,
// parts count, subtotal). Click the row (or the chevron) to expand and reveal
// editable fields: description, operations note, labour hours/rate, and the
// parts grid with qty / line-total / remove. Removing the whole work item
// remains accessible from the summary row.

const ProposedWorkItemCard = ({ wi }) => {
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState(false);
    const total = computeWorkItemTotal(wi);
    const partsCount = (wi.parts || []).length;

    const onField = (field, value) =>
        dispatch(editWorkItemField({ tempId: wi.tempId, field, value }));

    const onPartField = (partIndex, field, value) =>
        dispatch(editPartField({ tempId: wi.tempId, partIndex, field, value }));

    return (
        <div className="border rounded-xl bg-white overflow-hidden">
            {/* Summary row — always visible, click to expand */}
            <div
                role="button"
                tabIndex={0}
                onClick={() => setExpanded((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpanded((v) => !v);
                    }
                }}
                className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50/80 transition-colors ${
                    expanded ? 'border-b bg-gray-50/60' : ''
                }`}
            >
                <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                    <Wrench size={16} className="text-violet-600" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate" title={wi.description}>
                        {wi.description || <span className="text-gray-400 italic">Untitled work item</span>}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span className="truncate">{wi.intervalLabel}</span>
                        <span className="text-gray-300">·</span>
                        <span className="shrink-0 flex items-center gap-1">
                            <Package size={11} /> {partsCount} part{partsCount === 1 ? '' : 's'}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span className="shrink-0">{num(wi.labourHours)}h labour</span>
                    </div>
                </div>
                <p className="font-mono text-sm font-semibold text-gray-900 shrink-0">{money(total)}</p>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        dispatch(removeWorkItem(wi.tempId));
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                    title="Remove this work item from the import"
                >
                    <Trash2 size={15} />
                </button>
                <ChevronDown
                    size={16}
                    className={`text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
                />
            </div>

            {/* Expanded editor */}
            {expanded && (
                <>
                    {/* Description (editable) */}
                    <div className="px-4 py-3 border-b">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Description</p>
                        <input
                            type="text"
                            value={wi.description || ''}
                            onChange={(e) => onField('description', e.target.value)}
                            className="w-full border rounded-lg px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                    </div>

                    {/* Operations note */}
                    <div className="px-4 py-3 border-b">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Operations</p>
                        <textarea
                            value={wi.detailNotes || ''}
                            onChange={(e) => onField('detailNotes', e.target.value)}
                            rows={Math.min(8, Math.max(3, (wi.detailNotes || '').split('\n').length))}
                            className="w-full border rounded-lg px-2 py-1.5 text-xs font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                    </div>

                    {/* Labour */}
                    <div className="px-4 py-3 border-b grid grid-cols-3 gap-3">
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Hours</p>
                            <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={wi.labourHours ?? ''}
                                onChange={(e) => onField('labourHours', e.target.value)}
                                className="w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Rate / h</p>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={wi.labourRate ?? ''}
                                onChange={(e) => onField('labourRate', e.target.value)}
                                className="w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                        <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Labour</p>
                            <p className="px-2 py-1.5 text-sm font-mono text-gray-900">
                                {money(num(wi.labourHours) * num(wi.labourRate))}
                            </p>
                        </div>
                    </div>

                    {/* Parts */}
                    <div className="px-4 py-3">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                            <Package size={11} /> Parts ({partsCount})
                        </p>
                        {partsCount === 0 ? (
                            <p className="text-xs text-gray-400 italic">No parts allocated.</p>
                        ) : (
                            <div className="space-y-1.5">
                                {wi.parts.map((p, idx) => (
                                    <div key={`${p.partNumber}-${idx}`} className="grid grid-cols-12 gap-2 items-center text-sm">
                                        <p className="col-span-2 font-mono text-xs text-gray-600 truncate" title={p.partNumber}>
                                            {p.partNumber}
                                        </p>
                                        <p className="col-span-5 text-gray-800 truncate" title={p.description}>
                                            {p.description}
                                        </p>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={p.quantity ?? ''}
                                            onChange={(e) => onPartField(idx, 'quantity', e.target.value)}
                                            className="col-span-1 border rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:ring-2 focus:ring-violet-500"
                                        />
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={p.lineTotal ?? ''}
                                            onChange={(e) => onPartField(idx, 'lineTotal', e.target.value)}
                                            className="col-span-3 border rounded px-1.5 py-1 text-xs text-right font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
                                        />
                                        <button
                                            onClick={() => dispatch(removePart({ tempId: wi.tempId, partIndex: idx }))}
                                            className="col-span-1 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded justify-self-end"
                                            title="Remove this part"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Work-item subtotal */}
                    <div className="px-4 py-2 bg-gray-50 border-t flex items-center justify-between text-sm">
                        <span className="text-gray-500">Subtotal</span>
                        <span className="font-mono font-semibold text-gray-900">{money(total)}</span>
                    </div>
                </>
            )}
        </div>
    );
};

// ── RepcoImportModal ──────────────────────────────────────────────────────────

const RepcoImportModal = ({ job, onRefreshJob }) => {
    const dispatch = useDispatch();

    const { modalOpen, phase, preview, editedWorkItems, error } = useSelector((s) => s.repcoImport);

    // Local-only state for the upload step — kept out of redux because we
    // can't (and shouldn't) serialise File objects into the store.
    const [csvFile, setCsvFile] = useState(null);
    const [operationsText, setOperationsText] = useState('');

    // Reset local state whenever the modal closes — next open starts fresh.
    useEffect(() => {
        if (!modalOpen) {
            setCsvFile(null);
            setOperationsText('');
        }
    }, [modalOpen]);

    const handleClose = useCallback(() => {
        if (phase === 'committing') return; // don't allow close mid-write
        dispatch(closeModal());
    }, [dispatch, phase]);

    // Esc to close — only while open and not mid-commit
    useEscapeToClose(modalOpen && phase !== 'committing', handleClose);

    const handlePreview = async () => {
        if (!csvFile || !operationsText.trim() || !job?.uuid) return;
        dispatch(clearError());
        await dispatch(previewRepcoImport({
            jobUuid: job.uuid,
            csvFile,
            operationsText,
        }));
    };

    const handleBackToUpload = () => {
        // Clear the preview result and return to step 1. The user can re-paste
        // / re-upload and call preview again. The CSV File handle is kept so
        // they don't have to re-pick it.
        dispatch(closeModal());
        // Re-open immediately so the upload step shows again with the file
        // still set in local state.
        setTimeout(() => dispatch({ type: 'repcoImport/openModal' }), 0);
    };

    const handleCommit = async () => {
        if (!job?.uuid || !(editedWorkItems || []).length) return;
        dispatch(clearError());

        const mappedWorkItems = editedWorkItems.map((wi) => ({
            intervalLabel: wi.intervalLabel,
            description: wi.description,
            detailNotes: wi.detailNotes,
            labourHours: num(wi.labourHours),
            labourRate: num(wi.labourRate),
            parts: (wi.parts || []).map((p) => ({
                partNumber: p.partNumber,
                description: p.description,
                quantity: num(p.quantity),
                unitPrice: num(p.unitPrice),
                lineTotal: num(p.lineTotal),
            })),
            // Only gate on customer confirmation for priced items.
            // Repco $0 "Check/report" inspection lines (Horn, Wipers, etc.)
            // are workshop-only tasks — no customer sign-off needed, so no
            // amber "Awaiting response" pill on the Work Items tab.
            // Backend (RepcoImportService.commit) also clamps this against
            // the persisted cost detail as a defence in depth.
            confirmationRequired: computeWorkItemTotal(wi) > 0,
            confirmationMessage: null,
        }));

        // Roll unmapped CSV rows (fees, sundries, anything Repco couldn't
        // group under an interval) into a single trailing "Miscellaneous"
        // work item so nothing is silently dropped on commit. Each row
        // becomes a part with its original qty / unit price / line total
        // preserved. The user can edit or remove parts after import.
        const unmapped = preview?.unmappedCsvRows || [];
        if (unmapped.length > 0) {
            const miscParts = unmapped.map((row) => ({
                partNumber: row.partNumber,
                description: row.description,
                quantity: num(row.quantity),
                unitPrice: num(row.unitPrice),
                lineTotal: num(row.lineTotal),
            }));
            const miscTotal = miscParts.reduce((acc, p) => acc + p.lineTotal, 0);
            mappedWorkItems.push({
                intervalLabel: 'Miscellaneous',
                description: 'Miscellaneous',
                detailNotes: 'Fees, sundries, and other items from the quote that were not grouped under a service interval.',
                labourHours: 0,
                labourRate: 0,
                parts: miscParts,
                confirmationRequired: miscTotal > 0,
                confirmationMessage: null,
            });
        }

        const payload = {
            transitionToQuoted: true,
            proposedWorkItems: mappedWorkItems,
        };

        const result = await dispatch(commitRepcoImport({ jobUuid: job.uuid, payload }));
        // On success the slice closes the modal itself. We refresh the parent
        // job so the new work items appear on the Work Items tab.
        if (commitRepcoImport.fulfilled.match(result) && onRefreshJob) {
            onRefreshJob();
        }
    };

    if (!modalOpen) return null;

    const isPreviewing = phase === 'previewing';
    const isCommitting = phase === 'committing';

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[55] p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">
                {/* Header */}
                <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center shrink-0">
                            <Package size={18} className="text-violet-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Import from Repco</h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {phase === 'preview-ready' ? 'Review and edit the proposed work items'
                                    : isCommitting ? 'Creating work items…'
                                        : 'Upload the SmartQuote CSV and paste the service operations'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isCommitting}
                        className="p-2 hover:bg-gray-100 rounded-xl shrink-0 disabled:opacity-40"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body — phase router */}
                {(phase === 'idle' || isPreviewing) && (
                    <UploadStep
                        csvFile={csvFile}
                        setCsvFile={setCsvFile}
                        operationsText={operationsText}
                        setOperationsText={setOperationsText}
                        onCancel={handleClose}
                        onPreview={handlePreview}
                        isLoading={isPreviewing}
                        error={error}
                    />
                )}

                {(phase === 'preview-ready' || isCommitting) && preview && (
                    <PreviewStep
                        preview={preview}
                        editedWorkItems={editedWorkItems}
                        onBack={handleBackToUpload}
                        onCommit={handleCommit}
                        isCommitting={isCommitting}
                        error={error}
                    />
                )}
            </div>
        </div>
    );
};

export default RepcoImportModal;